import React from 'react';

import { formatMoney } from '@betterment/core';
import { Order } from '@betterment/shared-components/src/components/activity/components/drawer/transactionTypes/Order';
import { useFeatureFlag } from '@betterment/shared-components/src/components/feature-flag/useFeatureFlag';

import { ActivityReportsUrl } from './common/ActivityReportsUrl';
import { CancelButton } from './common/CancelButton';
import { AdvisoryFee } from './transactionTypes/AdvisoryFee';
import { AllocationChange } from './transactionTypes/AllocationChange';
import { Deposit } from './transactionTypes/Deposit';
import { Dividend } from './transactionTypes/Dividend';
import { Other } from './transactionTypes/Other';
import { Trade } from './transactionTypes/Trade';
import { Transfer } from './transactionTypes/Transfer';
import { Withdrawal } from './transactionTypes/Withdrawal';

import { ActivityItemFragment } from '../../../graphql/fragments/activityItem.generated';
import { IdempotencyProviderWithFeatureRegistry } from '../../../RetailApp/graphql/idempotency/IdempotencyProviderWithFeatureRegistry';

export const Drawer: React.FC<{ item: ActivityItemFragment }> = ({ item }) => {
  const { enabled: isCancelOrderEnabled } = useFeatureFlag({
    splitName: 'retail.self_directed_after_hours_order_cancellation_q4_2025_enabled',
    context: 'activity_drawer',
  });

  switch (item.__typename) {
    case 'ActivityItemDeposit':
      return <Deposit item={item} />;
    case 'ActivityItemWithdrawal':
      return <Withdrawal item={item} />;
    case 'ActivityItemOther':
      return <Other item={item} />;
    case 'ActivityItemTransferGroup':
      return <Transfer item={item} />;
    case 'ActivityItemTradeGroup':
      return <Trade item={item} />;
    case 'ActivityItemOrder':
      return (
        <Order
          item={item}
          activityReportsLink={
            item.activityReportsUrl ? <ActivityReportsUrl url={item.activityReportsUrl} /> : undefined
          }
          cancelAction={
            item.financialSecurityOrderId && item.isCancelable && isCancelOrderEnabled ? (
              <IdempotencyProviderWithFeatureRegistry featureName="cancelTransactionRequest">
                <CancelButton
                  transactionType="order"
                  transactionId={item.financialSecurityOrderId}
                  to={item.subAccount?.name ?? ''}
                  amount={item.amountTraded ? formatMoney(item.amountTraded) : ''}
                />
              </IdempotencyProviderWithFeatureRegistry>
            ) : undefined
          }
        />
      );
    case 'ActivityItemAllocationChange':
      return <AllocationChange item={item} />;
    case 'ActivityItemDividendGroup':
      return <Dividend item={item} />;
    case 'ActivityItemFee':
      return <AdvisoryFee item={item} />;
    case 'ActivityItemRollover':
      return <>Rollover</>;
    default: {
      const exhaustiveCheck: never = item;
      return exhaustiveCheck;
    }
  }
};
